import webApiAdmin from '@/lib/api-common/webApiAdmin';
import {
  clearIndexedDbCacheByPrefix,
  getIndexedDbCacheEntry,
  setIndexedDbCacheEntry,
  registerHeavyCache,
  INDEXED_CACHE_TTL_MS,
} from '@/lib/utils/indexedDbCache';

export interface Advertiser {
  _id: string;
  company?: string;
}

type AdvertiserListResponse = Advertiser[] | { advertisers?: Advertiser[] };

export interface GetAdvertisersOptions {
  onlyApproved?: boolean;
}

/** IndexedDB 캐시 키 접두사 — 헤더 TTL 표시·일괄 삭제 등에서 공유 */
export const ADVERTISER_CACHE_KEY_PREFIX = 'advertisers.getAdvertisers::';
const pendingAdvertiserRequests = new Map<string, Promise<Advertiser[]>>();

function getStableCacheValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(getStableCacheValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        const entryValue = (value as Record<string, unknown>)[key];
        if (typeof entryValue === 'undefined') {
          return acc;
        }

        acc[key] = getStableCacheValue(entryValue);
        return acc;
      }, {});
  }

  return value;
}

function getAdvertiserRequestParams(options?: GetAdvertisersOptions): Record<string, unknown> {
  return options ? { onlyApproved: options.onlyApproved } : {};
}

function getAdvertiserCacheKey(options?: GetAdvertisersOptions): string {
  return `${ADVERTISER_CACHE_KEY_PREFIX}${JSON.stringify(
    getStableCacheValue(getAdvertiserRequestParams(options)),
  )}`;
}

export async function clearAdvertiserCache(): Promise<void> {
  pendingAdvertiserRequests.clear();
  await clearIndexedDbCacheByPrefix(ADVERTISER_CACHE_KEY_PREFIX);
}

registerHeavyCache(ADVERTISER_CACHE_KEY_PREFIX, clearAdvertiserCache);

function normalizeAdvertisersPayload(data: AdvertiserListResponse | null | undefined): Advertiser[] {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.advertisers ?? [];
}

/** POST /api3/advertisers/getAdvertisers */
export async function getAdvertisers(
  options?: GetAdvertisersOptions,
  fetchOptions: { forceRefresh?: boolean } = {},
): Promise<Advertiser[]> {
  const params = getAdvertiserRequestParams(options) as {
    onlyApproved?: boolean;
  };
  const cacheKey = getAdvertiserCacheKey(options);
  const cachedEntry = fetchOptions.forceRefresh
    ? null
    : await getIndexedDbCacheEntry<Advertiser[]>(cacheKey);

  if (cachedEntry && cachedEntry.expiresAt > Date.now()) {
    return cachedEntry.value;
  }

  const pendingRequest = pendingAdvertiserRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
    try {
      const data = await webApiAdmin.post<AdvertiserListResponse>('admin/advertisers/list', params);
      const advertisers = normalizeAdvertisersPayload(data);
      if (advertisers.length > 0) {
        await setIndexedDbCacheEntry(cacheKey, advertisers, INDEXED_CACHE_TTL_MS);
      }
      return advertisers;
    } catch (error) {
      if (cachedEntry?.value) {
        return cachedEntry.value;
      }
      throw error;
    } finally {
      pendingAdvertiserRequests.delete(cacheKey);
    }
  })();

  pendingAdvertiserRequests.set(cacheKey, request);
  return request;
}

export async function getAdvertiserMap(
  options?: GetAdvertisersOptions,
  fetchOptions?: { forceRefresh?: boolean },
): Promise<Record<string, Advertiser>> {
  const advertisers = await getAdvertisers(options, fetchOptions ?? {});
  return advertisers.reduce<Record<string, Advertiser>>((acc, advertiser) => {
    if (!advertiser._id) {
      return acc;
    }

    acc[advertiser._id] = advertiser;
    return acc;
  }, {});
}
